import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Seo from '../components/Seo';
import StatusTimeline from '../components/StatusTimeline';
import DocumentPanel from '../components/DocumentPanel';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { portalTranslations } from '../i18n/portalTranslations';
import { getSupabase } from '../lib/supabase';
import {
  buildWhatsAppStatusLink,
  createShipment,
  getAllShipments,
  getStatusOrder,
  updateShipment,
  updateShipmentStatus,
} from '../services/shipmentService';
import { Shipment, ShipmentStatus } from '../types/shipment';
import './PortalPages.css';

const AdminPage: React.FC = () => {
  const { language } = useLanguage();
  const pt = portalTranslations[language].admin;
  const steps = portalTranslations[language].tracker.steps;
  const { user, loading, loginAdmin, logout } = useAuth();
  const [email, setEmail] = useState('info@alshiraa-customs.com');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [shipments, setShipments] = useState<Shipment[]>([]);
  const [loadingShipments, setLoadingShipments] = useState(false);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState({
    clientEmail: '',
    clientName: '',
    clientPhone: '',
    company: '',
    clientCode: '',
    cargoDescription: '',
    border: '',
    declarationNumber: '',
  });

  const [newShipment, setNewShipment] = useState({
    clientEmail: 'client@demo.com',
    clientName: 'أحمد التاجر',
    clientPhone: '',
    company: 'شركة النور للاستيراد',
    clientCode: 'T-0001',
    declarationNumber: '',
    cargoDescription: '',
    border: 'ميناء اللاذقية',
  });

  const [newClient, setNewClient] = useState({
    email: '',
    password: '',
    name: '',
    company: '',
    clientCode: '',
  });

  const loadShipments = useCallback(async () => {
    if (user?.role !== 'admin') return;
    setLoadingShipments(true);
    try {
      const rows = await getAllShipments();
      setShipments(rows);
    } finally {
      setLoadingShipments(false);
    }
  }, [user]);

  useEffect(() => {
    loadShipments();
  }, [loadShipments]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return shipments;
    return shipments.filter((s) =>
      s.declarationNumber.toLowerCase().includes(q) ||
      s.clientName.toLowerCase().includes(q) ||
      s.clientEmail.toLowerCase().includes(q) ||
      (s.clientCode || '').toLowerCase().includes(q) ||
      (s.company || '').toLowerCase().includes(q) ||
      s.cargoDescription.toLowerCase().includes(q) ||
      steps[s.status].toLowerCase().includes(q) ||
      s.status.includes(q)
    );
  }, [shipments, search, steps]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    const result = await loginAdmin(email, password);
    setSubmitting(false);

    if (!result.ok) {
      if (result.error === 'not_admin') {
        setError(pt.wrongRole);
      } else {
        setError(language === 'ar' ? 'بيانات الإدارة غير صحيحة' : 'Invalid admin credentials');
      }
    }
  };

  const handleStatusChange = async (shipment: Shipment, status: ShipmentStatus) => {
    setSavingId(shipment.id);
    const note = notes[shipment.id];
    const updated = await updateShipmentStatus(shipment.id, status, note);
    setSavingId(null);
    if (!updated) {
      setError(language === 'ar' ? 'تعذّر تحديث الحالة' : 'Could not update status');
      return;
    }
    setNotes((prev) => ({ ...prev, [shipment.id]: '' }));
    await loadShipments();

    const wa = buildWhatsAppStatusLink(
      updated.clientPhone || shipment.clientPhone,
      updated.declarationNumber,
      steps[status],
      note
    );
    if (wa) {
      window.open(wa, '_blank', 'noopener,noreferrer');
    }
  };

  const openWhatsApp = (shipment: Shipment) => {
    const wa = buildWhatsAppStatusLink(
      shipment.clientPhone,
      shipment.declarationNumber,
      steps[shipment.status],
      notes[shipment.id]
    );
    if (!wa) {
      setError(language === 'ar' ? 'أضف رقم واتساب العميل أولاً' : 'Add client WhatsApp phone first');
      return;
    }
    window.open(wa, '_blank', 'noopener,noreferrer');
  };

  const startEdit = (shipment: Shipment) => {
    setEditingId(shipment.id);
    setEditDraft({
      clientEmail: shipment.clientEmail,
      clientName: shipment.clientName,
      clientPhone: shipment.clientPhone || '',
      company: shipment.company || '',
      clientCode: shipment.clientCode || '',
      cargoDescription: shipment.cargoDescription,
      border: shipment.border,
      declarationNumber: shipment.declarationNumber,
    });
  };

  const saveEdit = async (id: string) => {
    setSavingId(id);
    const updated = await updateShipment(id, editDraft);
    setSavingId(null);
    if (!updated) {
      setError(language === 'ar' ? 'تعذّر حفظ التعديل' : 'Could not save edits');
      return;
    }
    setEditingId(null);
    await loadShipments();
  };

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newShipment.declarationNumber.trim()) return;

    setSubmitting(true);
    setError('');
    const created = await createShipment({
      clientEmail: newShipment.clientEmail,
      clientName: newShipment.clientName,
      clientPhone: newShipment.clientPhone,
      company: newShipment.company,
      clientCode: newShipment.clientCode,
      declarationNumber: newShipment.declarationNumber,
      cargoDescription: newShipment.cargoDescription,
      border: newShipment.border,
    });
    setSubmitting(false);

    if (!created) {
      setError(language === 'ar' ? 'تعذّر إضافة البيان. تحقق من رقم البيان.' : 'Could not add declaration.');
      return;
    }

    setInfo(pt.created);
    setNewShipment((prev) => ({ ...prev, declarationNumber: '', cargoDescription: '', clientPhone: '' }));
    await loadShipments();
  };

  const handleAddClient = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setInfo('');
    setSubmitting(true);

    const supabase = getSupabase();
    const session = supabase ? (await supabase.auth.getSession()).data.session : null;
    if (!session?.access_token) {
      setSubmitting(false);
      setError(language === 'ar' ? 'يجب تسجيل الدخول كإدارة' : 'Admin session required');
      return;
    }

    try {
      const res = await fetch('/.netlify/functions/create-client', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...newClient,
          accessToken: session.access_token,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Failed');
      } else {
        setInfo(language === 'ar' ? `تم إنشاء العميل: ${data.email} (${data.clientCode || newClient.clientCode})` : `Client created: ${data.email}`);
        setNewClient({ email: '', password: '', name: '', company: '', clientCode: '' });
      }
    } catch {
      setError(language === 'ar' ? 'تعذّر الاتصال بخدمة إنشاء العملاء' : 'Could not reach create-client function');
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="portal-page admin-page">
        <div className="container portal-inner portal-loading">
          {language === 'ar' ? 'جاري التحميل...' : 'Loading...'}
        </div>
      </div>
    );
  }

  return (
    <div className="portal-page admin-page">
      <Seo title={`${pt.title} | الشراع`} description={pt.subtitle} path="/admin" />
      <div className="container portal-inner">
        <h1>{pt.title}</h1>
        <p className="portal-subtitle">{pt.subtitle}</p>

        {user?.role !== 'admin' ? (
          <form className="portal-card portal-login" onSubmit={handleLogin}>
            <label>
              Email
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required dir="ltr" />
            </label>
            <label>
              {portalTranslations[language].portal.password}
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
            </label>
            {error && <p className="portal-error">{error}</p>}
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? (language === 'ar' ? 'جاري الدخول...' : 'Signing in...') : pt.login}
            </button>
            <p className="portal-demo">
              <Link to="/portal">{language === 'ar' ? 'بوابة العملاء' : 'Client portal'}</Link>
            </p>
            <Link to="/" className="portal-back">← {language === 'ar' ? 'العودة للموقع' : 'Back to site'}</Link>
          </form>
        ) : (
          <div className="portal-dashboard">
            <div className="portal-dashboard-header">
              <p>{user.name}</p>
              <button type="button" className="btn btn-secondary" onClick={() => logout()}>{pt.logout}</button>
            </div>

            {error && <p className="portal-error">{error}</p>}
            {info && <p className="portal-info">{info}</p>}

            <form className="portal-card admin-form" onSubmit={handleAddClient}>
              <h2>{pt.addClient}</h2>
              <div className="admin-form-grid">
                <input placeholder={pt.clientCode} value={newClient.clientCode} onChange={(e) => setNewClient({ ...newClient, clientCode: e.target.value })} dir="ltr" required />
                <input placeholder={pt.clientEmail} value={newClient.email} onChange={(e) => setNewClient({ ...newClient, email: e.target.value })} dir="ltr" required />
                <input placeholder={pt.clientPassword} type="password" value={newClient.password} onChange={(e) => setNewClient({ ...newClient, password: e.target.value })} required minLength={6} />
                <input placeholder={pt.clientName} value={newClient.name} onChange={(e) => setNewClient({ ...newClient, name: e.target.value })} required />
                <input placeholder={pt.company} value={newClient.company} onChange={(e) => setNewClient({ ...newClient, company: e.target.value })} />
              </div>
              <button type="submit" className="btn btn-primary" disabled={submitting}>{pt.addClient}</button>
            </form>

            <form className="portal-card admin-form" onSubmit={handleAdd}>
              <h2>{pt.addShipment}</h2>
              <div className="admin-form-grid">
                <input placeholder={pt.clientCode} value={newShipment.clientCode} onChange={(e) => setNewShipment({ ...newShipment, clientCode: e.target.value })} dir="ltr" required />
                <input placeholder={pt.clientEmail} value={newShipment.clientEmail} onChange={(e) => setNewShipment({ ...newShipment, clientEmail: e.target.value })} dir="ltr" required />
                <input placeholder={pt.clientName} value={newShipment.clientName} onChange={(e) => setNewShipment({ ...newShipment, clientName: e.target.value })} required />
                <input placeholder={pt.company} value={newShipment.company} onChange={(e) => setNewShipment({ ...newShipment, company: e.target.value })} />
                <input placeholder={pt.clientPhone} value={newShipment.clientPhone} onChange={(e) => setNewShipment({ ...newShipment, clientPhone: e.target.value })} dir="ltr" />
                <input placeholder={pt.declarationNumber} value={newShipment.declarationNumber} onChange={(e) => setNewShipment({ ...newShipment, declarationNumber: e.target.value })} dir="ltr" required />
                <input placeholder={pt.cargoDescription} value={newShipment.cargoDescription} onChange={(e) => setNewShipment({ ...newShipment, cargoDescription: e.target.value })} required />
                <input placeholder={pt.border} value={newShipment.border} onChange={(e) => setNewShipment({ ...newShipment, border: e.target.value })} required />
              </div>
              <button type="submit" className="btn btn-primary" disabled={submitting}>
                {submitting ? (language === 'ar' ? 'جاري الحفظ...' : 'Saving...') : pt.addShipment}
              </button>
            </form>

            <div className="portal-toolbar">
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={pt.search}
              />
            </div>

            <h2>{pt.allShipments}</h2>
            {loadingShipments ? (
              <p className="portal-empty">{language === 'ar' ? 'جاري تحميل البيانات...' : 'Loading declarations...'}</p>
            ) : (
              <div className="portal-shipment-list">
                {filtered.map((shipment) => (
                  <article key={shipment.id} className="portal-shipment-card">
                    <div className="portal-shipment-head">
                      <span className="portal-declaration" dir="ltr">{shipment.declarationNumber}</span>
                      {shipment.clientCode ? (
                        <span className="portal-client-code" dir="ltr">{shipment.clientCode}</span>
                      ) : null}
                      <span>{shipment.clientName}</span>
                    </div>
                    <p>{shipment.cargoDescription} — {shipment.border}</p>
                    <p className="portal-updated">
                      {shipment.company ? `${shipment.company} · ` : ''}
                      {shipment.clientEmail}
                      {shipment.clientPhone ? ` · ${shipment.clientPhone}` : ''}
                    </p>
                    <StatusTimeline current={shipment.status} language={language} compact history={shipment.history} showNotes />

                    {editingId === shipment.id ? (
                      <div className="admin-edit-grid">
                        <input value={editDraft.clientCode} onChange={(e) => setEditDraft({ ...editDraft, clientCode: e.target.value })} dir="ltr" placeholder={pt.clientCode} />
                        <input value={editDraft.declarationNumber} onChange={(e) => setEditDraft({ ...editDraft, declarationNumber: e.target.value })} dir="ltr" placeholder={pt.declarationNumber} />
                        <input value={editDraft.clientEmail} onChange={(e) => setEditDraft({ ...editDraft, clientEmail: e.target.value })} dir="ltr" placeholder={pt.clientEmail} />
                        <input value={editDraft.clientName} onChange={(e) => setEditDraft({ ...editDraft, clientName: e.target.value })} placeholder={pt.clientName} />
                        <input value={editDraft.company} onChange={(e) => setEditDraft({ ...editDraft, company: e.target.value })} placeholder={pt.company} />
                        <input value={editDraft.clientPhone} onChange={(e) => setEditDraft({ ...editDraft, clientPhone: e.target.value })} dir="ltr" placeholder={pt.clientPhone} />
                        <input value={editDraft.cargoDescription} onChange={(e) => setEditDraft({ ...editDraft, cargoDescription: e.target.value })} placeholder={pt.cargoDescription} />
                        <input value={editDraft.border} onChange={(e) => setEditDraft({ ...editDraft, border: e.target.value })} placeholder={pt.border} />
                        <button type="button" className="btn btn-primary" disabled={savingId === shipment.id} onClick={() => saveEdit(shipment.id)}>{pt.save}</button>
                        <button type="button" className="btn btn-secondary" onClick={() => setEditingId(null)}>{language === 'ar' ? 'إلغاء' : 'Cancel'}</button>
                      </div>
                    ) : (
                      <button type="button" className="btn btn-secondary admin-edit-btn" onClick={() => startEdit(shipment)}>{pt.edit}</button>
                    )}

                    <label className="admin-status-select">
                      {pt.note}
                      <input
                        value={notes[shipment.id] || ''}
                        onChange={(e) => setNotes((prev) => ({ ...prev, [shipment.id]: e.target.value }))}
                        placeholder={pt.note}
                      />
                    </label>
                    <label className="admin-status-select">
                      {pt.updateStatus}
                      <select
                        value={shipment.status}
                        disabled={savingId === shipment.id}
                        onChange={(e) => handleStatusChange(shipment, e.target.value as ShipmentStatus)}
                      >
                        {getStatusOrder().map((s) => (
                          <option key={s} value={s}>{steps[s]}</option>
                        ))}
                      </select>
                    </label>
                    <button type="button" className="btn btn-secondary" onClick={() => openWhatsApp(shipment)}>
                      {pt.notifyWhatsApp}
                    </button>

                    <DocumentPanel shipmentId={shipment.id} canUpload />
                  </article>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
